import {
  Alert,
  Card,
  Col,
  Empty,
  Row,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/lib/table";

import { useFetchIncomeTaxPreviewQuery } from "../../../../../../api/graphql/queries/incomeTaxPreview/incomeTaxPreview.generated";
import {
  IncomeTaxPreviewPositionGroup,
  IncomeTaxPreviewPositionStatus,
} from "../../../../../../api/graphql/schema.generated";
import type { FetchIncomeTaxPreviewQuery } from "../../../../../../api/graphql/queries/incomeTaxPreview/incomeTaxPreview.generated";
import { DEFAULT_LOCALE } from "../../../../../../constants";
import { IUser } from "../../../../../../types";
import { formatAmountCurrency } from "../../../../../../utils/amount";
import {
  GroupHeading,
  NoteButton,
  NoteIcon,
  PositionsTable,
  VisuallyHidden,
} from "./styledComponents";

type Position =
  FetchIncomeTaxPreviewQuery["incomeTaxPreview"]["positions"][number];

/**
 * A row is either a section heading or a position. Headings live in the data
 * rather than in three separate tables so the columns stay aligned down the
 * whole derivation.
 */
type DerivationRow =
  | { kind: "group"; key: string; group: IncomeTaxPreviewPositionGroup }
  | { kind: "position"; key: string; position: Position };

/**
 * Income tax and trade tax go to two different recipients and meet at exactly
 * one point, the § 35 credit. Named blocks say that; a flat list leaves it to
 * be inferred from the labels.
 */
const GROUP_LABELS: Record<IncomeTaxPreviewPositionGroup, string> = {
  [IncomeTaxPreviewPositionGroup.BASIS]: "Grundlagen",
  [IncomeTaxPreviewPositionGroup.TRADE_TAX]: "Gewerbesteuer (an die Gemeinde)",
  [IncomeTaxPreviewPositionGroup.INCOME_TAX]:
    "Einkommensteuer und Solidaritätszuschlag (an das Finanzamt)",
};

/** Fixed, so a block never appears above the one it depends on. */
const GROUP_ORDER = [
  IncomeTaxPreviewPositionGroup.BASIS,
  IncomeTaxPreviewPositionGroup.TRADE_TAX,
  IncomeTaxPreviewPositionGroup.INCOME_TAX,
];

const isMissing = (position: Position) =>
  position.status === IncomeTaxPreviewPositionStatus.MISSING;

/** The rest of the tab is formatted for a German reader; the rate has to match. */
const formatRate = (rate: number) =>
  `${new Intl.NumberFormat(DEFAULT_LOCALE, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(rate * 100)} %`;

/**
 * A rate of zero is a real answer: it is what a customer below the basic
 * allowance gets. An absent rate is not, and rendering it as 0,0 % would be the
 * "no data shown as a calculated zero" the derivation model exists to prevent,
 * on the most prominent figure in the tab. The dash matches the descriptive
 * rows in the table below.
 */
const describeRate = (rate?: number | null) =>
  rate == null ? "–" : formatRate(rate);

const describePeriod = (position: Position) =>
  position.periodFrom && position.periodTo
    ? `${position.periodFrom} bis ${position.periodTo}`
    : null;

/**
 * A caveat that explains a figure, at the figure. It is one hover away instead
 * of one of several paragraphs stacked above the table, where the ones that
 * always apply drown out the ones that only sometimes do.
 *
 * Hover is not the only way in. Ant Design's tooltip triggers on hover alone by
 * default, so a note behind one is unreachable by keyboard, and moving these
 * out of the page body would have taken them away from anyone not using a mouse
 * rather than merely tucking them away. The trigger is a real button, it opens
 * on focus as well, and the text sits in the accessible name so a screen reader
 * gets it without opening anything.
 */
const Note = ({ text, label }: { text: string; label: string }) => (
  <Tooltip
    title={text}
    trigger={["hover", "focus"]}
    overlayStyle={{ maxWidth: 420 }}
  >
    <NoteButton type="button">
      <NoteIcon aria-hidden />
      <VisuallyHidden>{`Hinweis zu ${label}: ${text}`}</VisuallyHidden>
    </NoteButton>
  </Tooltip>
);

const toRows = (positions: Position[]): DerivationRow[] =>
  GROUP_ORDER.flatMap((group) => {
    const inGroup = positions.filter((position) => position.group === group);

    if (!inGroup.length) {
      return [];
    }

    return [
      { kind: "group" as const, key: `group-${group}`, group },
      ...inGroup.map((position) => ({
        kind: "position" as const,
        key: position.key,
        position,
      })),
    ];
  });

/** A heading spans the table; its own columns collapse away underneath it. */
const spanHeading = (row: DerivationRow) =>
  row.kind === "group" ? { colSpan: 3 } : {};
const hideUnderHeading = (row: DerivationRow) =>
  row.kind === "group" ? { colSpan: 0 } : {};

const columns: ColumnsType<DerivationRow> = [
  {
    title: "Position",
    key: "label",
    width: 260,
    onCell: spanHeading,
    render: (_, row) => {
      if (row.kind === "group") {
        return <GroupHeading>{GROUP_LABELS[row.group]}</GroupHeading>;
      }

      const { label, note } = row.position;

      return (
        <>
          {label}
          {note && <Note text={note} label={label} />}
        </>
      );
    },
  },
  {
    title: "Betrag",
    key: "amount",
    align: "right",
    width: 140,
    onCell: hideUnderHeading,
    render: (_, row) => {
      if (row.kind === "group") {
        return null;
      }

      const { position } = row;

      if (isMissing(position)) {
        return <Tag color="orange">fehlt</Tag>;
      }

      /*
       * A descriptive row carries no amount. It stays visibly empty rather than
       * showing 0,00 EUR, which would read as a calculated zero.
       */
      if (position.amount == null) {
        return <span>&ndash;</span>;
      }

      return formatAmountCurrency(position.amount);
    },
  },
  {
    title: "Quelle",
    key: "source",
    onCell: hideUnderHeading,
    render: (_, row) => {
      if (row.kind === "group") {
        return null;
      }

      const { position } = row;

      if (isMissing(position)) {
        return position.reason;
      }

      const period = describePeriod(position);

      return [position.source, period].filter(Boolean).join(" · ");
    },
  },
];

/**
 * The preliminary income and trade tax calculation for one customer, read only.
 *
 * This writes nothing and triggers nothing. It exists so Tax Ops can answer
 * "how much of this is actually mine" and explain how the number came about
 * before any customer sees it.
 */
const IncomeTaxPreview = ({ user }: { user: IUser }) => {
  const { data, loading, error } = useFetchIncomeTaxPreviewQuery({
    variables: { email: user.email },
    fetchPolicy: "cache-and-network",
  });

  if (loading && !data) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spin />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="Die Vorschau konnte nicht geladen werden"
        description={error.message}
      />
    );
  }

  const preview = data?.incomeTaxPreview;

  if (!preview) {
    return <Empty description="Keine Vorschau verfügbar" />;
  }

  if (!preview.hasDataBasis) {
    const reason = preview.positions.find(isMissing)?.reason;

    return (
      <Empty
        description={
          reason ??
          `Für ${preview.periodFrom} bis ${preview.periodTo} gibt es keine Buchhaltungsdaten.`
        }
      />
    );
  }

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title={
                <>
                  Was ist deins?
                  {preview.whatIsYoursNote && (
                    <Note
                      text={preview.whatIsYoursNote}
                      label="Was ist deins?"
                    />
                  )}
                </>
              }
              value={formatAmountCurrency(preview.whatIsYours ?? undefined)}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Dein persönlicher Steuersatz"
              value={describeRate(preview.averageTaxRate)}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title={`Gewinn ${preview.periodFrom} bis ${preview.periodTo}`}
              value={formatAmountCurrency(preview.profit ?? undefined)}
            />
          </Card>
        </Col>
      </Row>

      {/*
       * Only the caveats that say the figure above is wrong. They appear when
       * an input is missing, so they are never part of the standing noise, and
       * a reader must not have to hover to find out the number is unusable.
       */}
      {preview.warnings.map((warning) => (
        <Alert
          key={warning}
          type="warning"
          showIcon
          message={warning}
          style={{ marginBottom: 8 }}
        />
      ))}

      {preview.burden && (
        <Card
          size="small"
          title={
            <>
              Steuerlast bisher
              {preview.burdenNote && (
                <Note text={preview.burdenNote} label="Steuerlast bisher" />
              )}
            </>
          }
          style={{ marginTop: 16, marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Finanzamt"
                valueStyle={{ fontSize: 20 }}
                value={formatAmountCurrency(preview.burden.taxOffice)}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title={
                  preview.burden.municipalityName
                    ? `Gemeinde ${preview.burden.municipalityName}`
                    : "Gemeinde"
                }
                valueStyle={{ fontSize: 20 }}
                value={formatAmountCurrency(preview.burden.municipality)}
              />
            </Col>
            <Col span={8}>
              <Statistic
                title="Summe"
                valueStyle={{ fontSize: 20 }}
                value={formatAmountCurrency(preview.burden.total)}
              />
            </Col>
          </Row>
        </Card>
      )}

      <Card size="small" title="Berechnung" bodyStyle={{ padding: 0 }}>
        <PositionsTable>
          <Table<DerivationRow>
            columns={columns}
            dataSource={toRows(preview.positions)}
            rowKey={(row) => row.key}
            rowClassName={(row) => {
              if (row.kind === "group") {
                return "income-tax-preview-row-group";
              }

              return isMissing(row.position)
                ? "income-tax-preview-row-missing"
                : "";
            }}
            pagination={false}
            size="small"
          />
        </PositionsTable>
      </Card>
    </div>
  );
};

export default IncomeTaxPreview;
